import { CURRENCY_NAMES, isShoreBasedMembershipType } from '@pya-mms/common';
import { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { Button, Skeleton, Text } from '../../../ui';
import { FlexContainer } from '../../../ui/Container';
import { MembershipCategory, MembershipTypeOption, WizardFormData } from '../types';

// Plan benefits data based on PYA website
const MEMBER_BENEFITS: Record<string, string[]> = {
  'New to Yachting': [
    'Career support and advice',
    'Industry updates and newsletter',
    'Networking events access',
    'PYA-negotiated discounts',
  ],
  'Shore Based': ['All New to Yachting benefits', 'Specifically designed for shore-based professionals'],
  Associate: [
    'All New to Yachting benefits',
    'Welfare support',
    'Membership Assistance Service (MAS)',
    'Document certification',
  ],
  Full: [
    'All Associate benefits',
    'MCA-approved PYA Service Record',
    'Sea Service Testimonial verification',
    'OOW 3000 Training Record Book',
  ],
};

const PARTNER_BENEFITS: Record<string, string[]> = {
  Support: [
    'Dedicated page on PYA website',
    'Spotlight post and story feature',
    '10% off on Full Membership',
    'Marketing material in office',
  ],
  Core: ['All Support benefits', 'Article feature on website', 'Social media promotion', 'Logo in PYA newsletter'],
  Strategic: ['All Core benefits', 'Logo on PYA homepage', 'Unlimited story reshares', '20% off on Full Membership'],
};

const PlansGrid = styled.div<{ $singleColumn?: boolean }>`
  display: grid;
  grid-template-columns: ${(p) => (p.$singleColumn ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))')};
  gap: 16px;
  width: 100%;
  max-width: 1000px;

  @media (max-width: ${(p) => p.theme.screen.sm}) {
    grid-template-columns: 1fr;
  }
`;

const PlanSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spacing.xs};
  width: 100%;
  max-width: 1000px;
`;

const SectionLabel = styled(Text)`
  font-size: ${(p) => p.theme.font.size.sm};
  font-weight: ${(p) => p.theme.font.weight.semibold};
  color: ${(p) => p.theme.colors.general.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const PlanCard = styled.div<{ $isSelected: boolean; $isPopular?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: ${(p) => p.theme.spacing.sm};
  border: 2px solid ${(p) => (p.$isSelected ? p.theme.colors.general.primary : p.theme.colors.border.pya)};
  border-radius: ${(p) => p.theme.radius.md};
  background-color: ${(p) => (p.$isSelected ? 'rgba(20, 118, 255, 0.05)' : p.theme.colors.general.white)};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${(p) => (p.$isPopular ? p.theme.shadows.sm : 'none')};

  &:hover {
    border-color: ${(p) => p.theme.colors.general.primary};
    box-shadow: ${(p) => p.theme.shadows.sm};
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -10px;
  right: 16px;
  background-color: ${(p) => p.theme.colors.general.success};
  color: white;
  padding: 4px 12px;
  border-radius: 0;
  font-size: ${(p) => p.theme.font.size.tiny};
  font-weight: ${(p) => p.theme.font.weight.semibold};
`;

const PlanTitle = styled(Text)`
  font-size: ${(p) => p.theme.font.size.md};
  font-weight: ${(p) => p.theme.font.weight.semibold};
  color: ${(p) => p.theme.colors.general.black};
`;

const PlanPrice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: ${(p) => p.theme.spacing.xxs} 0;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const PriceAmount = styled.span`
  font-size: ${(p) => p.theme.font.size.xl};
  font-weight: ${(p) => p.theme.font.weight.bold};
  color: ${(p) => p.theme.colors.general.primary};
`;

const PriceFrequency = styled.span`
  font-size: ${(p) => p.theme.font.size.xs};
  color: ${(p) => p.theme.colors.general.textSecondary};
`;

const DiscountBadge = styled.span`
  display: inline-block;
  background-color: ${(p) => p.theme.colors.chip.green.background};
  color: ${(p) => p.theme.colors.chip.green.color};
  padding: 2px 8px;
  border-radius: 0;
  font-size: ${(p) => p.theme.font.size.tiny};
  font-weight: ${(p) => p.theme.font.weight.semibold};
`;

const OriginalPrice = styled.span`
  font-size: ${(p) => p.theme.font.size.xs};
  color: ${(p) => p.theme.colors.grey[50]};
  text-decoration: line-through;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${(p) => p.theme.spacing.xs} 0 0 0;
  flex-grow: 1;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 0;
  font-size: ${(p) => p.theme.font.size.xxs};
  color: ${(p) => p.theme.colors.general.text};

  &::before {
    content: '✓';
    color: ${(p) => p.theme.colors.general.success};
    font-weight: bold;
    flex-shrink: 0;
  }
`;

const ButtonGroup = styled(FlexContainer)`
  margin-top: ${(p) => p.theme.spacing.sm};
`;

interface PlanSelectionStepProps {
  category: MembershipCategory;
  membershipTypes: MembershipTypeOption[];
  partnerTypes: MembershipTypeOption[];
  isLoading: boolean;
  onNext: () => void;
  onBack: () => void;
}

type GroupedPlanEntry = [string, MembershipTypeOption[]];

const sortPlansByLowestRate = (entries: GroupedPlanEntry[]): GroupedPlanEntry[] =>
  [...entries].sort(([, planA], [, planB]) => {
    const minA = Math.min(...planA.map((p) => p.rate));
    const minB = Math.min(...planB.map((p) => p.rate));
    return minA - minB;
  });

const getPlanGroupKey = (plan: MembershipTypeOption): string => plan.typeSlug || plan.title;

const isShoreBasedPlanGroup = (plans: MembershipTypeOption[]): boolean =>
  plans.some((plan) => isShoreBasedMembershipType(plan.title, plan.typeSlug));

const PlanSelectionStep: FC<PlanSelectionStepProps> = ({
  category,
  membershipTypes,
  partnerTypes,
  isLoading,
  onNext,
  onBack,
}) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<WizardFormData>();

  const selectedPlan = watch('membershipType');

  const plans = useMemo(() => {
    if (category === 'member') return membershipTypes;
    if (category === 'partner') return partnerTypes;
    return [];
  }, [category, membershipTypes, partnerTypes]);

  const groupedPlans = useMemo(() => {
    const groups: Record<string, MembershipTypeOption[]> = {};
    plans.forEach((plan) => {
      const key = getPlanGroupKey(plan);
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(plan);
    });
    return groups;
  }, [plans]);

  const { yachtCrewPlans, shoreBasedPlans } = useMemo(() => {
    const entries = Object.entries(groupedPlans) as GroupedPlanEntry[];
    if (category !== 'member') {
      return { yachtCrewPlans: sortPlansByLowestRate(entries), shoreBasedPlans: [] as GroupedPlanEntry[] };
    }
    const shore = sortPlansByLowestRate(entries.filter(([, planOptions]) => isShoreBasedPlanGroup(planOptions)));
    const crew = sortPlansByLowestRate(entries.filter(([, planOptions]) => !isShoreBasedPlanGroup(planOptions)));
    return { yachtCrewPlans: crew, shoreBasedPlans: shore };
  }, [groupedPlans, category]);

  const handleSelectPlan = (planId: string) => {
    setValue('membershipType', planId, { shouldValidate: true });
  };

  const getBenefits = (title: string, typeSlug?: string): string[] => {
    if (category === 'member') {
      if (isShoreBasedMembershipType(title, typeSlug)) {
        return MEMBER_BENEFITS['Shore Based'];
      }
      const key = Object.keys(MEMBER_BENEFITS).find((k) => title.toLowerCase().includes(k.toLowerCase()));
      return key ? MEMBER_BENEFITS[key] : [];
    }
    if (category === 'partner') {
      const key = Object.keys(PARTNER_BENEFITS).find((k) => title.toLowerCase().includes(k.toLowerCase()));
      return key ? PARTNER_BENEFITS[key] : [];
    }
    return [];
  };

  const isPopular = (title: string): boolean => {
    if (category === 'member') return title.toLowerCase().includes('full');
    if (category === 'partner') return title.toLowerCase().includes('core');
    return false;
  };

  const getCurrencyWord = (currency: string): string => {
    const currencyWords: Record<string, string> = {
      EUR: 'euros',
      USD: 'dollars',
      GBP: 'pounds',
    };
    return currencyWords[currency] || CURRENCY_NAMES[currency] || currency;
  };

  const calculateDiscount = (option: MembershipTypeOption, baseOption: MembershipTypeOption): number => {
    if (option.duration <= 1) return 0;
    const baseYearlyRate = baseOption.rate;
    const expectedTotal = baseYearlyRate * option.duration;
    const actualTotal = option.rate;
    const savings = expectedTotal - actualTotal;
    return Math.round((savings / expectedTotal) * 100);
  };

  const renderPlanCard = ([groupKey, planOptions]: GroupedPlanEntry) => {
    const displayTitle = planOptions[0]?.title ?? groupKey;
    const sortedOptions = [...planOptions].sort((a, b) => a.duration - b.duration);
    const baseOption = sortedOptions.find((p) => p.duration === 1) || sortedOptions[0];
    const selectedOption = sortedOptions.find((p) => p.id === selectedPlan) || baseOption;
    const isSelected = sortedOptions.some((p) => p.id === selectedPlan);
    const benefits = getBenefits(displayTitle, planOptions[0]?.typeSlug);
    const popular = isPopular(displayTitle);
    const currentDiscount = calculateDiscount(selectedOption, baseOption);

    return (
      <PlanCard
        key={groupKey}
        $isSelected={isSelected}
        $isPopular={popular}
        onClick={() => handleSelectPlan(baseOption.id)}
      >
        {popular && <PopularBadge>Popular</PopularBadge>}
        <PlanTitle>{displayTitle}</PlanTitle>
        <PlanPrice>
          <PriceRow>
            <PriceAmount>{selectedOption.rate}</PriceAmount>
            <PriceFrequency>
              {getCurrencyWord(selectedOption.currency)}/
              {selectedOption.duration > 1 ? `${selectedOption.duration} years` : 'year'}
            </PriceFrequency>
          </PriceRow>
          {currentDiscount > 0 && (
            <PriceRow>
              <OriginalPrice>
                {baseOption.rate * selectedOption.duration} {getCurrencyWord(baseOption.currency)}
              </OriginalPrice>
              <DiscountBadge>Save {currentDiscount}%</DiscountBadge>
            </PriceRow>
          )}
        </PlanPrice>

        {sortedOptions.length > 1 && (
          <FlexContainer gap="xxs" style={{ marginTop: '8px', flexWrap: 'wrap' }}>
            {sortedOptions.map((option) => {
              const optionDiscount = calculateDiscount(option, baseOption);
              return (
                <Button
                  key={option.id}
                  type="button"
                  size="xs"
                  color={selectedPlan === option.id ? 'primary' : 'transparent'}
                  textColor={selectedPlan === option.id ? 'white' : 'textSecondary'}
                  border={selectedPlan === option.id ? 'none' : '1px solid #dee2e6'}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectPlan(option.id);
                  }}
                  style={{ padding: '4px 8px', height: 'auto' }}
                >
                  {option.duration} year plan
                  {optionDiscount > 0 && ` (-${optionDiscount}%)`}
                </Button>
              );
            })}
          </FlexContainer>
        )}

        <BenefitsList>
          {benefits.map((benefit, index) => (
            <BenefitItem key={index}>{benefit}</BenefitItem>
          ))}
        </BenefitsList>
      </PlanCard>
    );
  };

  if (isLoading) {
    return (
      <FlexContainer flexDirection="column" gap="md">
        <Skeleton count={3} height={200} />
      </FlexContainer>
    );
  }

  return (
    <FlexContainer flexDirection="column" gap="md" align="center">
      <FlexContainer flexDirection="column" align="center" gap="xxs">
        <Text weight="semibold" size="lg" style={{ textAlign: 'center' }}>
          Choose your {category === 'partner' ? 'partnership' : 'membership'} plan
        </Text>
        <Text color="textSecondary" size="sm" style={{ textAlign: 'center' }}>
          Select the plan that best fits your needs
        </Text>
      </FlexContainer>

      {category === 'member' ? (
        <FlexContainer flexDirection="column" gap="md" align="center" style={{ width: '100%' }}>
          {yachtCrewPlans.length > 0 && (
            <PlanSection>
              <SectionLabel>For yacht crew</SectionLabel>
              <PlansGrid>{yachtCrewPlans.map(renderPlanCard)}</PlansGrid>
            </PlanSection>
          )}
          {shoreBasedPlans.length > 0 && (
            <PlanSection>
              <SectionLabel>For shore-based professionals</SectionLabel>
              <PlansGrid $singleColumn>{shoreBasedPlans.map(renderPlanCard)}</PlansGrid>
            </PlanSection>
          )}
        </FlexContainer>
      ) : (
        <PlansGrid>{yachtCrewPlans.map(renderPlanCard)}</PlansGrid>
      )}

      {errors.membershipType && (
        <Text color="error" size="xs">
          {errors.membershipType.message}
        </Text>
      )}

      <ButtonGroup gap="sm" justify="center">
        <Button type="button" color="transparent" textColor="textSecondary" onClick={onBack}>
          Back
        </Button>
        <Button type="button" color="primary" onClick={onNext} disabled={!selectedPlan} style={{ minWidth: '150px' }}>
          Continue
        </Button>
      </ButtonGroup>
    </FlexContainer>
  );
};

export default PlanSelectionStep;
